import type { GraphOperationAddNode } from '@onefootprint/request-types/dashboard';
import { useTranslation } from 'react-i18next';
import type { NodeIconProps } from '../../base-node/components/node-icon';
import { nodeIconConfigs } from '../../base-node/components/node-icon-config';

export type NewNodeGroup =
  | 'data'
  | 'document'
  | 'decision'
  | 'advanced'
  | 'custom'
  | 'ai'
  | 'branch'
  | 'recipe'
  | 'internal';

// when a new group is added or modified, TS will enforce that we add it to this record
export const newNodeGroupValues: Record<NewNodeGroup, NewNodeGroup> = {
  recipe: 'recipe',
  data: 'data',
  document: 'document',
  branch: 'branch',
  decision: 'decision',
  advanced: 'advanced',
  custom: 'custom',
  ai: 'ai',
  internal: 'internal',
};

type NewNodeKindContext = {
  title: string;
  description: string;
  icon: NodeIconProps;
  group: NewNodeGroup;
  priority: number;
  isBeta?: boolean;
};

const useGetNewNodeContext = () => {
  const { t } = useTranslation('playbook-builder');

  // Because this is a record (vs. a Partial<>) we should throw an error if
  // a new value is added to nodeKind and we don't handle it exhaustively
  const nodeKindToContext: Record<GraphOperationAddNode['nodeKind'], NewNodeKindContext> = {
    // Data collection
    collect_identity_data: {
      title: t($ => $.addNode.nodes.collectIdentityData),
      description: t($ => $.addNode.descriptions.collectIdentityData),
      icon: nodeIconConfigs.identityData,
      group: 'data',
      priority: 0,
    },
    collect_business_data: {
      title: t($ => $.addNode.nodes.collectBusinessData),
      description: t($ => $.addNode.descriptions.collectBusinessData),
      icon: nodeIconConfigs.businessData,
      group: 'data',
      priority: 1,
    },
    custom_data: {
      title: t($ => $.addNode.nodes.collectCustomData),
      description: t($ => $.addNode.descriptions.collectCustomData),
      icon: nodeIconConfigs.customData,
      group: 'data',
      priority: 2,
    },
    authentication: {
      title: t($ => $.addNode.nodes.authentication),
      description: t($ => $.addNode.descriptions.authentication),
      icon: nodeIconConfigs.authentication,
      group: 'data',
      priority: 3,
    },
    investor_profile: {
      title: t($ => $.addNode.nodes.investorProfile),
      description: t($ => $.addNode.descriptions.investorProfile),
      icon: nodeIconConfigs.investorProfile,
      group: 'data',
      priority: 4,
    },
    collect_card_data: {
      title: t($ => $.addNode.nodes.collectCardData),
      description: t($ => $.addNode.descriptions.collectCardData),
      icon: nodeIconConfigs.collectCard,
      group: 'data',
      priority: 5,
    },
    bank_link: {
      title: t($ => $.addNode.nodes.bankLink),
      description: t($ => $.addNode.descriptions.bankLink),
      icon: nodeIconConfigs.bankLink,
      group: 'data',
      priority: 6,
    },

    // Document collection
    identity_document: {
      title: t($ => $.addNode.nodes.identityDocument),
      description: t($ => $.addNode.descriptions.identityDocument),
      icon: nodeIconConfigs.identityDocument,
      group: 'document',
      priority: 0,
    },
    custom_document: {
      title: t($ => $.addNode.nodes.customDocument),
      description: t($ => $.addNode.descriptions.customDocument),
      icon: nodeIconConfigs.customDocument,
      group: 'document',
      priority: 1,
    },
    proof_of_address: {
      title: t($ => $.addNode.nodes.proofOfAddress),
      description: t($ => $.addNode.descriptions.proofOfAddress),
      icon: nodeIconConfigs.proofOfAddress,
      group: 'document',
      priority: 2,
    },
    proof_of_ssn: {
      title: t($ => $.addNode.nodes.proofOfSsn),
      description: t($ => $.addNode.descriptions.proofOfSsn),
      icon: nodeIconConfigs.proofOfSsn,
      group: 'document',
      priority: 3,
    },
    selfie: {
      title: t($ => $.addNode.nodes.selfie),
      description: t($ => $.addNode.descriptions.selfie),
      icon: nodeIconConfigs.selfie,
      group: 'document',
      priority: 4,
    },

    // New branches for rules nodes
    empty_branch: {
      title: t($ => $.addNode.nodes.addBranch),
      description: t($ => $.addNode.descriptions.addBranch),
      icon: nodeIconConfigs.emptyBranch,
      group: 'branch',
      priority: 0,
    },
    collect_identity_document_stepup: {
      title: t($ => $.addNode.nodes.collectIdentityDocumentStepup),
      description: t($ => $.addNode.descriptions.collectIdentityDocumentStepup),
      icon: nodeIconConfigs.proofOfSsn,
      group: 'branch',
      priority: 1,
    },
    custom_document_stepup: {
      title: t($ => $.addNode.nodes.customDocumentStepup),
      description: t($ => $.addNode.descriptions.customDocumentStepup),
      icon: nodeIconConfigs.customDocument,
      group: 'branch',
      priority: 2,
    },
    proof_of_address_stepup: {
      title: t($ => $.addNode.nodes.proofOfAddressStepup),
      description: t($ => $.addNode.descriptions.proofOfAddressStepup),
      icon: nodeIconConfigs.proofOfAddress,
      group: 'branch',
      priority: 3,
    },
    proof_of_ssn_stepup: {
      title: t($ => $.addNode.nodes.proofOfSsnStepup),
      description: t($ => $.addNode.descriptions.proofOfSsnStepup),
      icon: nodeIconConfigs.proofOfSsn,
      group: 'branch',
      priority: 4,
    },

    // Decision
    verification_check: {
      title: t($ => $.addNode.nodes.verificationCheck),
      description: t($ => $.addNode.descriptions.verificationCheck),
      icon: nodeIconConfigs.verificationCheck,
      group: 'decision',
      priority: 0,
    },
    rules: {
      title: t($ => $.addNode.nodes.rules),
      description: t($ => $.addNode.descriptions.conditionalSubtree),
      icon: nodeIconConfigs.rules,
      group: 'decision',
      priority: 1,
    },
    branch: {
      title: t($ => $.addNode.nodes.branch),
      description: t($ => $.addNode.descriptions.branch),
      icon: nodeIconConfigs.branch,
      group: 'decision',
      priority: 3,
    },
    action: {
      title: t($ => $.addNode.nodes.action),
      description: t($ => $.addNode.descriptions.action),
      icon: nodeIconConfigs.action,
      group: 'decision',
      priority: 4,
    },

    // Advanced
    external_api: {
      title: t($ => $.addNode.nodes.externalApi),
      description: t($ => $.addNode.descriptions.externalApi),
      icon: nodeIconConfigs.externalApi,
      group: 'advanced',
      priority: 1,
    },
    vendor_integration: {
      title: t($ => $.addNode.nodes.vendorIntegration),
      description: t($ => $.addNode.descriptions.vendorIntegration),
      icon: nodeIconConfigs.vendorIntegration,
      group: 'advanced',
      priority: 2,
    },
    prerequisite: {
      title: t($ => $.addNode.nodes.prerequisite),
      description: t($ => $.addNode.descriptions.prerequisite),
      icon: nodeIconConfigs.prerequisite,
      group: 'advanced',
      priority: 3,
    },
    output: {
      title: t($ => $.addNode.nodes.output),
      description: t($ => $.addNode.descriptions.output),
      icon: nodeIconConfigs.output,
      group: 'advanced',
      priority: 8,
    },
    complete: {
      title: t($ => $.addNode.nodes.complete),
      description: t($ => $.addNode.descriptions.complete),
      icon: nodeIconConfigs.complete,
      group: 'advanced',
      priority: 9,
    },
    assign_data: {
      title: t($ => $.addNode.nodes.assignData),
      description: t($ => $.addNode.descriptions.assignData),
      icon: nodeIconConfigs.assignData,
      group: 'advanced',
      priority: 4,
    },
    common_expression: {
      title: t($ => $.addNode.nodes.commonExpression),
      description: t($ => $.addNode.descriptions.commonExpression),
      icon: nodeIconConfigs.commonExpression,
      group: 'advanced',
      priority: 5,
    },
    code_execution: {
      title: t($ => $.addNode.nodes.codeExecution),
      description: t($ => $.addNode.descriptions.codeExec),
      icon: nodeIconConfigs.codeExec,
      group: 'advanced',
      priority: 6,
    },
    verified_prefill: {
      title: t($ => $.addNode.nodes.verifiedPrefill),
      description: t($ => $.addNode.descriptions.verifiedPrefill),
      icon: nodeIconConfigs.verifiedPrefill,
      group: 'advanced',
      priority: 7,
    },

    // Percy AI
    ai_decision_generic: {
      title: t($ => $.addNode.nodes.aiAgentNode),
      description: t($ => $.addNode.descriptions.aiAgentNode),
      icon: nodeIconConfigs.aiAgent,
      group: 'ai',
      priority: 0,
    },

    // Recipes
    ssn_miskey: {
      title: t($ => $.addNode.nodes.ssnMiskey),
      description: t($ => $.addNode.descriptions.ssnMiskey),
      icon: nodeIconConfigs.ssnMiskey,
      group: 'recipe',
      priority: 2,
    },
    under_age_branch: {
      title: t($ => $.addNode.nodes.underAgeBranch),
      description: t($ => $.addNode.descriptions.underAgeBranch),
      icon: nodeIconConfigs.underAgeBranch,
      group: 'recipe',
      priority: 3,
    },
    country_kyc_id_doc_branch: {
      title: t($ => $.addNode.nodes.countryKycIdDocBranch),
      description: t($ => $.addNode.descriptions.countryKycIdDocBranch),
      icon: nodeIconConfigs.countryKycIdDoc,
      group: 'recipe',
      priority: 4,
    },
    ein_document_verification: {
      title: t($ => $.addNode.nodes.einDocumentVerification),
      description: t($ => $.addNode.descriptions.einDocumentVerification),
      icon: nodeIconConfigs.einDocument,
      group: 'recipe',
      priority: 1,
    },
    document_name_matching: {
      title: t($ => $.addNode.nodes.documentNameMatching),
      description: t($ => $.addNode.descriptions.documentNameMatching),
      icon: nodeIconConfigs.documentNameMatching,
      group: 'recipe',
      priority: 5,
    },
    identity_document_reupload: {
      title: t($ => $.addNode.nodes.identityDocumentReupload),
      description: t($ => $.addNode.descriptions.identityDocumentReupload),
      icon: nodeIconConfigs.identityDocument,
      group: 'recipe',
      priority: 0,
    },
    basic_kyc: {
      title: t($ => $.addNode.nodes.basicKyc),
      description: t($ => $.addNode.descriptions.basicKyc),
      icon: nodeIconConfigs.basicKyc,
      group: 'recipe',
      priority: 1,
    },
    basic_kyb: {
      title: t($ => $.addNode.nodes.basicKyb),
      description: t($ => $.addNode.descriptions.basicKyb),
      icon: nodeIconConfigs.basicKyb,
      group: 'recipe',
      priority: 1,
    },
  };

  const getUnknownNodeContext = (kind: GraphOperationAddNode['nodeKind'] | `agent_${string}`): NewNodeKindContext => ({
    title: kind,
    description: '-',
    icon: nodeIconConfigs.action,
    group: 'internal',
    priority: 999,
  });

  const getNodeContext = (kind: GraphOperationAddNode['nodeKind']): NewNodeKindContext => {
    if (kind in nodeKindToContext) return nodeKindToContext[kind];
    return getUnknownNodeContext(kind);
  };

  const isValidNodeKind = (kind: string) => kind in nodeKindToContext;

  return { getNodeContext, isValidNodeKind };
};

export default useGetNewNodeContext;
